/*
 * The Gulmohar Co. — canonical global cart drawer.
 *
 * Backs snippets/cart-drawer-tgc.liquid (rendered once in layout/theme.liquid
 * and once in layout/theme.pdp.liquid). Any trigger element anywhere in the
 * theme can open this drawer by carrying a `data-cart-drawer-trigger`
 * attribute — no per-page wiring needed.
 *
 * Also listens for the theme's global 'cart:update' event (dispatched as
 * CartAddEvent/CartUpdateEvent by assets/product-form.js from product cards,
 * Quick View, and anywhere else in the theme) so an add-to-cart anywhere
 * refreshes and opens this same drawer instead of any other cart UI.
 */
(() => {
  "use strict";

  const cartDrawer = document.getElementById("pdpCartDrawer");
  const cartBackdrop = document.getElementById("pdpCartBackdrop");
  const cartClose = document.getElementById("pdpCartClose");
  const cartItemsEl = document.getElementById("pdpCartItems");
  const cartTotalEl = document.getElementById("pdpCartTotal");
  const cartSubtotalEl = document.getElementById("pdpCartSubtotal");
  const cartDiscountsEl = document.getElementById("pdpCartDiscounts");
  const cartRewardsEl = document.getElementById("pdpCartRewards");
  const cartRewardsMessageEl = document.getElementById("pdpCartRewardsMessage");
  const cartHeadCountEl = document.getElementById("pdpCartHeadCount");
  const cartHeadNounEl = document.getElementById("pdpCartHeadNoun");
  const cartCheckoutEl = document.getElementById("pdpCartCheckout");
  const cartShippingEl = document.getElementById("pdpCartShipping");
  const cartShippingMessageEl = document.getElementById("pdpCartShippingMessage");
  const cartShippingFillEl = document.getElementById("pdpCartShippingFill");
  const cartStatusEl = document.getElementById("pdpCartStatus");

  if (!cartDrawer || !cartBackdrop) return;

  const autoOpen = cartDrawer.dataset.autoOpen === "true";
  const currency = cartDrawer.dataset.currency || "INR";
  /** Shopify's shop.money_format template, e.g. "Rs. {{amount_with_comma_separator}}" — matches the server-rendered `| money` output exactly. */
  const moneyFormatTemplate = cartDrawer.dataset.moneyFormat || "{{amount}}";

  /** Replicates Shopify's `money` liquid filter client-side, given cents. */
  function moneyFormat(cents) {
    return moneyFormatTemplate.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      let thousandsSeparator = ",";
      let decimalSeparator = ".";
      let precision = 2;

      switch (placeholder) {
        case "amount":
          break;
        case "amount_no_decimals":
          precision = 0;
          break;
        case "amount_with_comma_separator":
          thousandsSeparator = ".";
          decimalSeparator = ",";
          break;
        case "amount_no_decimals_with_comma_separator":
          thousandsSeparator = ".";
          precision = 0;
          break;
        case "amount_with_apostrophe_separator":
          thousandsSeparator = "'";
          break;
        case "currency":
          return currency;
        default:
          break;
      }

      const rounded = (cents / 100).toFixed(precision);
      let [whole, fraction] = rounded.split(".");
      whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
      return precision <= 0 ? whole : `${whole}${decimalSeparator}${(fraction || "").padEnd(precision, "0")}`;
    });
  }

  const bagCountEls = [document.getElementById("pdpBagCount"), cartHeadCountEl].filter(Boolean);

  let returnFocus = null;
  let previousOverflow = "";
  const drawerTriggers = () => document.querySelectorAll("[data-cart-drawer-trigger]");
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const openBag = (opener = document.activeElement) => {
    if (cartDrawer.classList.contains("is-open")) return;
    returnFocus = opener instanceof HTMLElement && !cartDrawer.contains(opener)
      ? opener
      : document.querySelector("[data-cart-drawer-trigger]");
    previousOverflow = document.body.style.overflow;
    cartDrawer.inert = false;
    cartBackdrop.hidden = false;
    cartDrawer.setAttribute("aria-hidden", "false");
    cartDrawer.classList.add("is-open");
    drawerTriggers().forEach((trigger) => trigger.setAttribute("aria-expanded", "true"));
    document.body.style.overflow = "hidden";
    document.body.classList.add("tgc-cart-open");
    cartClose?.focus({ preventScroll: true });
  };

  const closeBag = () => {
    if (!cartDrawer.classList.contains("is-open")) return;
    cartDrawer.classList.remove("is-open");
    cartDrawer.setAttribute("aria-hidden", "true");
    cartDrawer.inert = true;
    cartBackdrop.hidden = true;
    drawerTriggers().forEach((trigger) => trigger.setAttribute("aria-expanded", "false"));
    document.body.style.overflow = previousOverflow;
    document.body.classList.remove("tgc-cart-open");
    const target = returnFocus?.isConnected ? returnFocus : document.querySelector("[data-cart-drawer-trigger]");
    returnFocus = null;
    target?.focus({ preventScroll: true });
  };

  drawerTriggers().forEach((trigger) => {
    trigger.setAttribute("aria-controls", cartDrawer.id);
    trigger.setAttribute("aria-expanded", "false");
  });

  document.addEventListener("click", (e) => {
    const trigger = e.target.closest("[data-cart-drawer-trigger]");
    if (trigger) {
      e.preventDefault();
      openBag(trigger);
    }
  });

  if (cartClose) cartClose.addEventListener("click", closeBag);
  if (cartBackdrop) cartBackdrop.addEventListener("click", closeBag);

  document.addEventListener("keydown", (e) => {
    if (!cartDrawer.classList.contains("is-open")) return;
    if (e.key === "Escape") {
      e.preventDefault();
      closeBag();
      return;
    }
    if (e.key !== "Tab") return;
    const elements = [...cartDrawer.querySelectorAll(focusableSelector)]
      .filter((el) => el.getClientRects().length > 0);
    const first = elements[0];
    const last = elements[elements.length - 1];
    if (!first) {
      e.preventDefault();
      cartDrawer.focus({ preventScroll: true });
    } else if (e.shiftKey && (document.activeElement === first || !cartDrawer.contains(document.activeElement))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !cartDrawer.contains(document.activeElement))) {
      e.preventDefault();
      first.focus();
    }
  });

  document.addEventListener("focusin", (e) => {
    if (cartDrawer.classList.contains("is-open") && !cartDrawer.contains(e.target)) {
      cartClose?.focus({ preventScroll: true });
    }
  });

  /** Renders cart line items + total + counts from a Shopify /cart.js payload. */
  function renderCart(cart) {
    bagCountEls.forEach((el) => {
      el.textContent = cart.item_count;
    });
    if (cartHeadNounEl) cartHeadNounEl.textContent = cart.item_count === 1 ? "piece" : "pieces";

    document.dispatchEvent(new CustomEvent("cart:count-update", {
      detail: { data: { itemCount: cart.item_count, source: "cart-drawer-tgc" } }
    }));

    if (cartTotalEl) cartTotalEl.textContent = moneyFormat(cart.total_price);
    if (cartSubtotalEl) cartSubtotalEl.textContent = moneyFormat(cart.original_total_price);

    if (cartRewardsEl) {
      const level = Math.min(cart.item_count, 3);
      cartRewardsEl.hidden = cart.item_count === 0;
      cartRewardsEl.dataset.level = String(level);
      const progress = cartRewardsEl.querySelector('[role="progressbar"]');
      if (progress) progress.setAttribute("aria-valuenow", String(level));
      if (cartRewardsMessageEl) {
        cartRewardsMessageEl.textContent = level === 1
          ? "5% off this piece. Add 1 more for 10% off"
          : level === 2
            ? "10% off these pieces. Add 1 more for 15% off"
            : "15% off 3 or more pieces";
      }
    }

    if (cartShippingEl) {
      const threshold = Number(cartShippingEl.dataset.threshold);
      const total = Number(cart.items_subtotal_price);
      cartShippingEl.hidden = cart.item_count === 0;
      if (Number.isFinite(threshold) && threshold > 0 && Number.isFinite(total)) {
        const remaining = Math.max(0, threshold - total);
        if (cartShippingMessageEl) {
          cartShippingMessageEl.textContent = remaining
            ? moneyFormat(remaining) + " more for free shipping in India at ₹2,499"
            : "Free shipping threshold reached for delivery in India";
        }
        const progress = cartShippingEl.querySelector('[role="progressbar"]');
        const capped = Math.min(threshold, Math.max(0, total));
        if (progress) {
          progress.setAttribute("aria-valuenow", String(Math.floor(capped / 100)));
          progress.setAttribute("aria-valuetext", String(Math.floor(capped / threshold * 100)) + "% of threshold");
        }
        if (cartShippingFillEl) cartShippingFillEl.style.width = String(capped / threshold * 100) + "%";
      }
    }
    if (cartStatusEl) {
      cartStatusEl.textContent = cart.item_count === 0
        ? "Your bag is empty"
        : cart.item_count + (cart.item_count === 1 ? " item" : " items") + " in your bag. Total " + moneyFormat(cart.total_price);
    }

    if (cartDiscountsEl) {
      cartDiscountsEl.replaceChildren();
      const itemSavings = Math.max(0, Number(cart.original_total_price) - Number(cart.items_subtotal_price));
      const cartSavings = Math.max(0, Number(cart.items_subtotal_price) - Number(cart.total_price));
      const applications = cart.cart_level_discount_applications || [];
      const discounts = [];
      if (itemSavings > 0) discounts.push({ title: "Item savings", amount: itemSavings });

      const amounts = applications.map((application) => Number(application.total_allocated_amount));
      const canShowSeparately = applications.length > 0
        && amounts.every(Number.isFinite)
        && amounts.reduce((sum, amount) => sum + amount, 0) === cartSavings;

      const tierRate = cart.item_count >= 3 ? 15 : cart.item_count === 2 ? 10 : 5;
      const tierTitle = cart.item_count >= 3
        ? "Buy 3+ Pieces — 15% Off"
        : cart.item_count === 2
          ? "Buy 2 Pieces — 10% Off"
          : "Buy 1 Piece — 5% Off";
      const expectedTierSavings = Math.round(Number(cart.items_subtotal_price) * tierRate / 100);
      const fallbackTitle = Math.abs(cartSavings - expectedTierSavings) <= 1
        ? tierTitle
        : "Cart discount";

      if (canShowSeparately) {
        applications.forEach((application, index) => {
          const title = application.title || application.discount_application?.title || fallbackTitle;
          discounts.push({ title, amount: amounts[index] });
        });
      } else if (cartSavings > 0) {
        const title = applications.length === 1
          ? applications[0].title || applications[0].discount_application?.title || fallbackTitle
          : fallbackTitle;
        discounts.push({ title, amount: cartSavings });
      }

      for (const discount of discounts) {
        const row = document.createElement("div");
        row.className = "pdp-cart-breakdown-row pdp-cart-breakdown-row--discount";
        const copy = document.createElement("span");
        copy.className = "pdp-cart-discount-copy";
        const title = document.createElement("span");
        title.textContent = discount.title;
        copy.append(title);
        if (discount.title.toLowerCase().includes("piece") && discount.title.toLowerCase().includes("% off")) {
          const badge = document.createElement("small");
          badge.textContent = "Applied automatically";
          copy.append(badge);
        }
        const amount = document.createElement("span");
        amount.textContent = "−" + moneyFormat(discount.amount);
        row.append(copy, amount);
        cartDiscountsEl.append(row);
      }
    }

    if (cartCheckoutEl) cartCheckoutEl.disabled = cart.item_count === 0;

    if (!cartItemsEl) return;

    if (cart.item_count === 0) {
      cartItemsEl.innerHTML = '<p class="pdp-cart-empty">Your bag is empty.</p>';
      return;
    }

    cartItemsEl.innerHTML = cart.items
      .map((item, index) => {
        const line = index + 1;
        const escapeHTML = (value) => String(value ?? "").replace(/[&<>"']/g, (ch) => ({
          "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
        })[ch]);
        const imageStyle = item.image
          ? `background-image:url('${item.image}'); background-size:cover; background-position:center;`
          : "background:#C4A67A;";
        const variantBit = item.product_has_only_default_variant
          ? ""
          : `${escapeHTML(item.variant_title)} &middot; `;

        return `
          <div class="pdp-cart-item" data-line="${line}">
            <div class="pdp-cart-item-img" style="${imageStyle}"><div class="pdp-art-glow-sm"></div></div>
            <div>
              <div class="pdp-cart-item-cat">${escapeHTML(item.product_type)}</div>
              <div class="pdp-cart-item-name">${escapeHTML(item.product_title)}</div>
              <div class="pdp-cart-item-meta">${variantBit}Qty ${item.quantity}</div>
              <div class="pdp-cart-item-qty">
                <button class="pdp-cart-item-qty-btn" type="button" data-line="${line}" data-action="decrease" aria-label="Decrease quantity of ${escapeHTML(item.product_title)}">&minus;</button>
                <span class="pdp-cart-item-qty-val" data-line="${line}">${item.quantity}</span>
                <button class="pdp-cart-item-qty-btn" type="button" data-line="${line}" data-action="increase" aria-label="Increase quantity of ${escapeHTML(item.product_title)}">&plus;</button>
              </div>
            </div>
            <div class="pdp-cart-item-right">
              <div class="pdp-cart-item-price">${moneyFormat(item.final_line_price)}</div>
              <button class="pdp-cart-item-remove" type="button" data-line="${line}" aria-label="Remove ${escapeHTML(item.product_title)}">Remove</button>
            </div>
          </div>
        `;
      })
      .join("");
  }

  function refreshCart() {
    return fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        renderCart(cart);
        return cart;
      });
  }

  function changeLine(line, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line: Number(line), quantity }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Unable to update your bag");
        return refreshCart();
      })
      .then((cart) => {
        document.dispatchEvent(
          new CustomEvent("cart:update", { bubbles: true, detail: { data: { itemCount: cart.item_count, source: "cart-drawer-tgc" } } })
        );
        return cart;
      });
  }

  if (cartItemsEl) {
    cartItemsEl.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".pdp-cart-item-remove");
      const qtyBtn = e.target.closest(".pdp-cart-item-qty-btn");

      if (removeBtn) {
        const line = removeBtn.dataset.line;
        if (!line) return;
        removeBtn.disabled = true;
        changeLine(line, 0).catch(() => {
          removeBtn.disabled = false;
        });
        return;
      }

      if (qtyBtn) {
        const line = qtyBtn.dataset.line;
        const valEl = cartItemsEl.querySelector(`.pdp-cart-item-qty-val[data-line="${line}"]`);
        if (!line || !valEl) return;

        let qty = parseInt(valEl.textContent, 10) || 1;
        qty += qtyBtn.dataset.action === "increase" ? 1 : -1;
        if (qty < 0) qty = 0;

        qtyBtn.disabled = true;
        changeLine(line, qty).finally(() => {
          qtyBtn.disabled = false;
        });
      }
    });
  }

  /* Any add-to-cart elsewhere in the theme (product cards, Quick View, and
     this drawer's own AJAX changes above) dispatches this same event. */
  document.addEventListener("cart:update", (e) => {
    if (e.detail?.data?.source === "cart-drawer-tgc") return; // already rendered above
    if (e.detail?.data?.didError) return;

    refreshCart().then(() => {
      if (autoOpen) openBag();
    });
  });
})();
